/**
 * Wann ein Vorgang WIRKLICH auf Zustellung wartet.
 *
 * `dispatch_status: "queued"` allein genügt nicht. Ein zurückgestellter oder
 * bereits erledigter Vorgang behält seinen Warteschlangen-Eintrag, kann ihn
 * aber nie einlösen — der Verteiler befördert nur, was auch offen ist.
 *
 * Issue hat das am 12.08. an echten Daten gegengerechnet, als ich die Zahl für
 * mein Abzeichen anforderte:
 *
 *     dispatch_status='queued' roh                              13
 *     davon tatsächlich zustellbar (+ status open/in_progress)   0
 *
 * Alle dreizehn waren Reste — zwölf zurückgestellt, einer erledigt. Serverseitig
 * ist es seither korrekt gezählt; die Oberfläche trug dieselbe Falle aber noch
 * an drei Stellen als Etikett „wartend".
 *
 * Der Grund, warum so etwas lange überlebt: **Eine Zahl, die zu hoch ist, sieht
 * aus wie Arbeit, nicht wie ein Defekt.** Niemand meldet ein Abzeichen, das
 * mehr anzeigt — man arbeitet es ab und wundert sich, dass nichts kommt.
 */
export interface DispatchableIssue {
  status?: string;
  dispatch_status?: string | null;
  /** Serverseitig abgeleitet (Issue, 12.08.) — die ehrliche Antwort auf
   *  „wartet dieser Vorgang wirklich". Vorbild ist `current_stage`. */
  is_queued?: boolean;
}

/** Zustände, aus denen der Verteiler befördern kann. */
export const DISPATCHABLE_STATUSES: readonly string[] = ['open', 'in_progress'];

export function isReallyQueued(issue: DispatchableIssue | null | undefined): boolean {
  if (!issue) return false;
  // Die Antwort des Servers schlägt die lokale Regel. Sie IST die Regel:
  // `queued UND offen` ist Issues Fachlogik, und eine Kopie davon in meinem
  // Code weicht still ab, sobald sie sich dort ändert — etwa wenn `blocked`
  // eines Tages mitzählen soll.
  if (typeof issue.is_queued === 'boolean') return issue.is_queued;
  // Rückfall ohne Stichtag. Ein hartes Umschalten hiesse: Ein Client ohne das
  // Feld sähe GAR KEINE Wartenden mehr — aus einer Zahl, die zu hoch war,
  // würde eine, die zu niedrig ist.
  //
  // DIESE ZEILEN DÜRFEN WEG, SOBALD: AppDev `is_queued` in
  // `AgentIssueSummary` aufgenommen hat (iOS, #568-Scope). Beide
  // issue-api-Instanzen führen das Feld seit 12.08.; iOS ist der letzte
  // Leser, der es noch nicht kennt — und dort zeigt dieselbe Fehlerklasse
  // aktuell 13 falsche Wartende (12 zurückgestellt, 1 erledigt) gegen 0
  // echte, gezählt von Issue am 12.08.
  //
  // Die Bedingung steht hier und nicht in einem Gedächtnis, weil heute
  // dreimal eine Entscheidung überlebt hat, deren Voraussetzung gewandert
  // war — ohne dass irgendwo stand, wovon sie abhing.
  return issue.dispatch_status === 'queued'
    && DISPATCHABLE_STATUSES.includes(String(issue.status));
}
